// eslint-disable-next-line no-unused-vars
import React, { createElement, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { bool, object, array, func } from 'prop-types';
import { Grid, Row, Column, Button, Accordion, AccordionItem } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import {
  Laptop,
  Monitor,
  PcCase,
  WashingMachine,
  House,
  ShieldCheck,
  BriefcaseBusiness,
  Shapes,
  Volleyball,
  LampWallDown,
  Lamp,
  Thermometer,
  Microwave,
  HardHat,
  Armchair,
  GraduationCap,
  CreditCard,
  Circle,
  X,
  Menu,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  BellRing,
  Server,
} from 'lucide-react';

import useRouter from 'Hooks/useRouter';
import { Can } from 'Components/shared';
import { PERMISSION } from 'Constants';

const StyledButton = styled(Button)`
  &:hover,
  &:active,
  &:focus {
    color: ${ props => props.theme.colors.darkBlue };
    background-color: ${ props => props.theme.colors.shadowGray };
    outline: 0;
    border: 0;
  }

  &.CategoriesMenuButton {
    display: flex;
    padding: 0 1.5em;
    align-items: center;
    justify-content: flex-start;
    color: ${ props => props.theme.colors.darkBlue };
    background-color: ${ props => props.theme.colors.shadowGray };
    max-width: 260px;
    width: 100%;
    height: 40px;
    outline: 0;
    border: 0;
    text-align: left;
    font-size: 1em;
    font-weight: 500;
    z-index: ${ props => props.open ? 8999 : 'auto' };
  }

  svg {
    margin-right: 15px;
  }

  @media screen and (max-width: 767px) and (max-width: 1024px) {
    &.CategoriesMenuButton {
      &.isOpen {
        background: #0F233C !important;
      }
    }
  }

  @media screen and (max-width: 767px) {
    &.CategoriesMenuButton {
      &.isOpen {
        background: #D52B1E !important;
      }
    }
  }
`;

const CategoriesContainer = styled.div`
  position: absolute;
  top: 6px;
  display: flex;
  width: ${ props => props.categoryMenuOpen ? 'calc(100vw - 4rem)' : '260px' };
  box-shadow: 0 30px 65px ${ props => props.theme.colors.shadowGray };
  border-top: 1px solid ${ props => props.theme.colors.shadowGray };
  border-left: 1px solid ${ props => props.theme.colors.shadowGray };
  max-width: calc(1920px - 4em);
  padding: 0;
  z-index: ${ props => props.open ? 8998 : -1 };
  opacity: ${ props => props.open ? 1 : 0 };
  visibility: ${ props => props.open ? 'visible' : 'hidden' };
  background-color: ${ props => props.theme.colors.shadowGray };
  height: 50vh;
  @media screen and (min-width: 640px) and (max-width: 1055px) {
    height: 60vh;
  }
  @media screen and (min-width: 1056px) {
    height: 70vh;
  }

  @keyframes subnavHoverHelp {
    0% {
      width: 100%;
    }
    99% {
      width: 100%;
    }
    100% {
      width: 0;
    }
  }

  .menu {
    position: relative;
    top: 48px;
    width: 100%;
    min-width: 250px;
    max-width: 260px;
    border-bottom: 1px solid ${ props => props.theme.colors.shadowGray };
    background-color: #F6F6F6;
    overflow: auto;

    > li {
      &.active:hover::before,
      &.active:hover::after {
        content: "";
        position: absolute;
        z-index: 8999;
        bottom: 0;
        height: 80%;
        animation: subnavHoverHelp 1.2s;
        opacity: .3;
      }

      &.active:hover::before {
        top: calc(-100% + 12px);
        clip-path: polygon(0% 100%, 100% 0%, 100% 100%);
      }
      &.active:hover::after {
        bottom: calc(-100% + 12px);
        clip-path: polygon(100% 0%, 0% 0%, 100% 100%);
      }

      &:hover, &.active {
        background-color: #EBEBEB;
        color: ${ props => props.theme.colors.altOrange };
        font-weight: 600;

        a svg {
          transform: rotate(180deg);
        }
      }

      color: #6A6767;

      display: flex;
      align-items: center;
      position: relative;
      padding: 10px 15px;
      width: 100%;
      border-bottom: 1px solid #DBDBDB;

      .iconContainer {
        display: flex;
        justify-content: center;
        width: 35px;
        margin-right: 8px;
      }

      a {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex: 1;
        gap: 8px;
        color: inherit;
        font-size: 0.875rem;
        line-height: 1.125rem;

        svg {
          min-width: 24px;
        }
      }
    }
  }

  .menuContainer {
    position: relative;
    bottom: -47px;
    margin-top: -47px;
    background-color: ${ props => props.theme.colors.white };
    width: ${ props => props.categoryMenuOpen ? '100%' : 'auto' };
    overflow: auto;
    border-right: 1px solid ${ props => props.theme.colors.shadowGray };
    border-bottom: 1px solid ${ props => props.theme.colors.shadowGray };
  }
`;

const CategoriesContainerMobile = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  display: flex;
  width: 100vw;
  padding: 0;
  z-index: ${ props => props.open ? 8998 : -1 };
  opacity: ${ props => props.open ? 1 : 0 };
  visibility: ${ props => props.open ? 'visible' : 'hidden' };
  background-color: ${ props => props.theme.colors.white };
  height: calc(100vh - 67px);

  .menu {
    position: relative;
    top: 48px;
    width: 100%;
    min-width: 250px;
    border-bottom: 1px solid ${ props => props.theme.colors.shadowGray };
    overflow: auto;

    > button {
      color: ${ props => props.theme.colors.darkGray };
      display: flex;
      align-items: center;
      position: relative;
      padding: 15px;
      width: 100%;
      border: none;
      background: #fff;
      cursor: pointer;

      &:hover, &.active {
        color: ${ props => props.theme.colors.default };
      }

      .iconContainer {
        display: flex;
        justify-content: center;
        width: 35px;
        margin-right: 8px;
      }

      .level1 {
        color: inherit;
        font-size: 0.875rem;
        line-height: 1.125rem;
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        svg {
          transform: rotate(270deg);
        }
      }
    }

    .generalMenu {
      background: #F3F2F2;
      padding: 20px 0;
      display: flex;
      flex-direction: column;

      a {
        color: #3A3A3A;
        padding: 15px;
        font-size: 0.875rem;
        line-height: 1.125rem;
      }

      .cds--accordion__item {
        border: none;

        &.cds--accordion__item--active {
          .cds--accordion__content {
            display: flex;
            flex-direction: column;
            padding: 0 15px;
          }

          button {
            background: #E9E9E9;
          }
        }

        .cds--accordion__heading {
          padding: 15px 0;

          .cds--accordion__title {
            font-size: 0.875rem;
            line-height: 1.125rem;
          }
        }
      }
    }
  }

  .menuContainer {
    position: absolute;
    top: 0;
    background: #fff;
    width: 100vw;
    height: 100vh;
    z-index: 1;
    /* transform: translateX(100vw); */

    > button {
      color: #3A3A3A;
      display: flex;
      align-items: center;
      position: relative;
      padding: 15px;
      width: 100%;
      border: none;
      background: #fff;
      cursor: pointer;
      justify-content: space-between;

      svg {
        transform: rotate(270deg);
      }
    }

    .backButton {
      justify-content: flex-start;
      background: #F5F7F9;
      font-weight: 600;

      svg {
        margin-right: 12px;
        transform: rotate(90deg);
      }
    }

    li.level3 {
      color: ${ props => props.theme.colors.darkGray };
      display: flex;
      align-items: center;
      position: relative;
      width: 100%;

      a {
        font-size: 0.875rem;
        line-height: 1.125rem;
        width: 100%;
        color: inherit;
        padding: 15px;
        width: 100%;
      }
    }

    .cds--accordion__item {
      border: none;

      &.cds--accordion__item--active {

        .lucide-icon {
          transform: rotate(90deg);
        }

        .cds--accordion__heading {
          background: #E7ECF2;
        }

        .cds--accordion__content {
          background: #fff;
          padding: 0 15px;
        }
      }

      .cds--accordion__arrow {
        display: none !important;
      }

      .accordion-title-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding-right: 1rem;

        .cds--accordion__title {
          margin: 0;
          width: 100%;
        }

        .lucide-icon {
          transition: transform 0.11s cubic-bezier(0.2, 0, 0.38, 0.9);
        }
      }

      .cds--accordion__heading {
        font-size: 0.875rem;
        line-height: 1.125rem;
        padding: 15px 0;
      }
    }
  }
`;

const StyledGrid = styled(Grid)`
  width: 100%;
  display: flex;

  .title {
    &:after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 27%;
      height: 2px;
      background-color: ${ props => props.theme.colors.default };
      content: '';
    }

    &:before {
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 2px;
      background-color: ${ props => props.theme.products.colors.border };
      content: '';
    }

    display: -webkit-box;
    max-height: 40px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    position: relative;
    color: ${ props => props.theme.colors.darkGray };
    margin-bottom: 10px;
    padding-bottom: 10px;
    font-size: 0.875em;
    font-weight: 600;
    line-height: 1.25em;
    overflow: hidden;
    text-transform: uppercase;

    a {
      color: inherit;
    }
  }

  ul li {
    margin: 0.5rem 0;

    a {
      &:hover {
        font-weight: 600;
      }

      font-size: 0.875rem;
      color: ${ props => props.theme.colors.darkGray };
      line-height: 1rem
    }
  }

  .cds--row {
    flex-grow: 1;
  }

  .cds--css-grid-column {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .active {
    font-weight: 600;
  }
`;

const CategoryIconMap = new Map();
CategoryIconMap.set('laptop-icon', Laptop);
CategoryIconMap.set('tv-icon', Monitor);
CategoryIconMap.set('computer-icon', PcCase);
CategoryIconMap.set('large-appliance-icon', WashingMachine);
CategoryIconMap.set('house-icon', House);
CategoryIconMap.set('security-icon', ShieldCheck);
CategoryIconMap.set('professional-solutions-icon', BriefcaseBusiness);
CategoryIconMap.set('kid-toy-icon', Shapes);
CategoryIconMap.set('basketball-icon', Volleyball);
CategoryIconMap.set('bulb-icon', LampWallDown);
CategoryIconMap.set('lamp-icon', Lamp);
CategoryIconMap.set('thermometer-icon', Thermometer);
CategoryIconMap.set('small-appliance-icon', Microwave);
CategoryIconMap.set('safety-helmet-icon', HardHat);
CategoryIconMap.set('furniture-icon', Armchair);
CategoryIconMap.set('education-icon', GraduationCap);
CategoryIconMap.set('pos-icon', CreditCard);
CategoryIconMap.set('bell-ring', BellRing);
CategoryIconMap.set('server', Server);
CategoryIconMap.set(null, Circle);

const CategoriesMenu = forwardRef((props, ref) => {
  const {
    categories,
    open,
    activeCategory,
    openCategory,
    onToggle,
    onOpenCategory,
    isMobile,
    isExternalReseller,
    canAccessAccountStatements,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { location, query } = useRouter();
  const sortOrder = query?.order_by ? `order_by=${query?.order_by}&order_direction=${query?.order_direction}` : '';
  const perPage = query?.count ? `count=${query?.count}` : '';
  let querySortPerPage = '';

  if (location.pathname.includes('/products') && query?.order_by && query?.count) {
    querySortPerPage = `?${perPage}&${sortOrder}`;
  } else if (location.pathname.includes('/products') && query?.order_by && query?.count) {
    querySortPerPage = `?${sortOrder}`;
  } else if (query?.count) {
    querySortPerPage = `?${perPage}`;
  }

  return (
    <>
      <StyledButton
        theme={ theme }
        onClick={ () => onToggle(!open) }
        onMouseOver={ () => isMobile ? {} : onToggle(true) }
        onMouseLeave={ evt => isMobile ? {} : onToggle(false, evt) }
        className={ `CategoriesMenuButton ${ open ? 'isOpen' : '' }` }
        open={ open }
      >
        { open ? <X /> : <Menu /> } { t('navigation.products') }
      </StyledButton>
      { !isMobile ? (
        <CategoriesContainer
          className="CategoriesMenu grid-container"
          ref={ ref }
          onMouseLeave={ () => onToggle(false) }
          categoryMenuOpen={ openCategory }
          open={ open }
        >
          <nav className="menu">
            { categories.map(category => {
              const fn = () => onOpenCategory(category.id);
              const catIcon = CategoryIconMap.get(category.icon);
              return (
                <li
                  key={ category.id }
                  className={ openCategory?.id === category.id ? 'active' : '' }
                  onMouseOver={ fn }
                  onFocus={ fn }
                >
                  { catIcon ? (
                    <div className="iconContainer">
                      { createElement(catIcon) }
                    </div>
                  ) : null }
                  <Link to={ `/products/${ category.slug }-c${ category.id }/c${querySortPerPage}` }>
                    { category.name }
                    <ChevronLeft />
                  </Link>
                </li>
              );
            }) }
          </nav>
          <div className="menuContainer">
            { openCategory && (
              <StyledGrid key={ openCategory.id }>
                <Row>
                  { openCategory
                    .children
                    .filter(childCategory => childCategory?.children?.length)
                    .map(childCategory => (
                      <Column key={ childCategory.id }>
                        <h3 className="title">
                          <Link to={ `/products/${ childCategory.slug }-c${ childCategory.id }/c${querySortPerPage}` }>
                            { childCategory.name }
                          </Link>
                        </h3>
                        <ul>
                          { childCategory.children.map(cat => (
                            <li key={ cat.id }>
                              <Link
                                to={ `/products/${ cat.slug }-c${ cat.id }/c${querySortPerPage}` }
                                className={ activeCategory?.id === cat.id ? 'active' : '' }
                              >
                                { cat.name }
                              </Link>
                            </li>
                          )) }
                        </ul>
                      </Column>
                    )) }
                </Row>
              </StyledGrid>
            ) }
          </div>
        </CategoriesContainer>
      ) : (
        <CategoriesContainerMobile
          className="CategoriesMenu grid-container"
          ref={ ref }
          categoryMenuOpen={ openCategory }
          onClick={ evt => onToggle(false, evt) }
          open={ open }
        >
          <nav className="menu">
            { categories.map(category => {
              const fn = () => onOpenCategory(category.id);
              const catIcon = CategoryIconMap.get(category.icon);
              return (
                <button
                  type="button"
                  key={ category.id }
                  className={ openCategory?.id === category.id ? 'active' : '' }
                  onClick={ fn }
                >
                  { catIcon ? (
                    <div className="iconContainer">
                      { createElement(catIcon) }
                    </div>
                  ) : null }
                  <div className="level1">
                    <span>{ category.name }</span>
                    <ChevronDown />
                  </div>
                </button>
              );
            }) }
            <Accordion className="generalMenu">
              <Link to="/">{ t('navigation.home') }</Link>
              <Link to={ `/products${querySortPerPage}` }>{ t('navigation.products') }</Link>
              <AccordionItem
                title={ t('navigation.promotions') }
              >
                { !isExternalReseller && (
                  <>
                    <Link to="/products?only_resealed=1">{ t('navigation.resealed') }</Link>
                    <Link to="/promotions">{ t('navigation.weeklyPromotions') }</Link>
                  </>
                ) }
                <Link to="/products?stock-clearing-sale=1">{ t('navigation.stockClearingSale') }</Link>
                <Link to="/products?only_promotional=1">{ t('navigation.promotions') }</Link>
              </AccordionItem>
              <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
                <Link to="/orders">{ t('navigation.orders') }</Link>
                <Link to="/reports/invoices">{ t('navigation.invoices') }</Link>
                 { canAccessAccountStatements && (
                   <Link to="/reports/account-statements">{ t('users.menu.accountStatements') }</Link>
                 ) }
                <Link to="/api-integrations/api-information">{ t('navigation.api') }</Link>
                <Link to="/account/info">{ t('navigation.info') }</Link>
              </Can>
            </Accordion>
          </nav>
          { openCategory && (
            <div className="menuContainer">
              <button
                className="backButton"
                type="button"
                key="backButton"
                onClick={ () => onOpenCategory(null) }
              >
                <ChevronDown />
                <span>{ openCategory.name }</span>
              </button>
              <Accordion className="custom-lucide-accordion">
                { openCategory
                  .children
                  .filter(childCategory => childCategory?.children?.length)
                  .map(childCategory => (
                    <AccordionItem
                      key={ childCategory.id }
                      type="button"
                      // onClick={ () => onOpenSubCategory(childCategory.id) }
                      title={ (
                        <div className="accordion-title-wrapper">
                          <span>{ childCategory.name }</span>
                          <ChevronRight className="lucide-icon" size={ 24 } />
                        </div>
                      ) }
                    >
                      <ul>
                        { childCategory.children.map(cat => (
                          <li className="level3" key={ cat.id }>
                            <Link
                              to={ `/products/${ cat.slug }-c${ cat.id }/c${querySortPerPage}` }
                              className={ activeCategory?.id === cat.id ? 'active' : '' }
                            >
                              { cat.name }
                            </Link>
                          </li>
                        )) }
                      </ul>
                    </AccordionItem>
                  )) }
              </Accordion>
            </div>
          ) }
        </CategoriesContainerMobile>
      )}
    </>
  );
});

CategoriesMenu.propTypes = {
  open: bool,
  categories: array,
  activeCategory: object,
  openCategory: object,
  onToggle: func,
  onOpenCategory: func,
  isMobile: bool,
  isExternalReseller: bool,
  canAccessAccountStatements: bool,
};
CategoriesMenu.displayName = 'CategoriesMenuComponent';

export default CategoriesMenu;
